import { useQuery } from '@tanstack/react-query';
import { PROJECT_ROLE_LABEL } from '@planning/contracts';
import { expectedProgress, trafficLight } from '@planning/domain';
import { VisorTexto } from '@xiltrion/ui/bloques/editor-texto';
import { ProgresoCircular, Semaforo, TarjetaIndicador } from '@xiltrion/ui/bloques/indicadores';
import { AvatarPersona } from '@xiltrion/ui/bloques/personas';
import { Card, CardContent, CardHeader, CardTitle } from '@xiltrion/ui/card';
import { Icono } from '@xiltrion/ui/icono';
import { Timeline, TimelineContent, TimelineDate, TimelineHeader, TimelineIndicator, TimelineItem, TimelineSeparator, TimelineTitle } from '@xiltrion/ui/timeline';
import { apiGet } from '@/api/client';
import { usePersonas } from '@/api/hooks';
import type { ActivityRow } from '@/api/types';
import type { ViewProps } from '@/app/extension-points';
import { Slot } from '@/app/Slot';
import { useSession, useTerms } from '@/app/session';
import { describeEvent } from '../tasks/activity-text';
import { formatDate, isOverdue, today } from '../tasks/view-logic';

/** Resumen del proyecto: avance, semáforo, fechas, equipo, objetivos vinculados, paneles de los paquetes y actividad. */
export function OverviewView({ project, tasks }: ViewProps) {
  const { me, has } = useSession();
  const { t } = useTerms();
  const { porId: people } = usePersonas();
  const { data: activity = [] } = useQuery({ queryKey: ['project-activity', project?.id], queryFn: () => apiGet<ActivityRow[]>(`/projects/${project!.id}/activity`), enabled: !!project });
  const { data: links = [] } = useQuery({ queryKey: ['goal-links', project?.id], queryFn: () => apiGet<Array<{ link: { id: string }; title: string; code: string | null }>>('/goals/links', { projectId: project!.id }), enabled: !!project && has('core.goals') });
  if (!project) return null;
  const top = tasks.filter((x) => !x.parentId);
  const done = top.filter((x) => x.completedAt).length;
  const progress = top.length ? done / top.length : 0;
  const expected = project.startDate && project.dueDate ? expectedProgress(project.startDate, project.dueDate, today()) : null;
  const light = expected !== null ? trafficLight(progress, expected, me?.trafficLight) : null;
  return (
    <div className="grid gap-6 p-6 lg:grid-cols-3">
      <div className="space-y-6 lg:col-span-2">
        <div className="grid gap-4 sm:grid-cols-3">
          <TarjetaIndicador titulo="Avance" valor={`${Math.round(progress * 100)}%`} progreso={progress} semaforo={light} icono="mdi:chart-bar" detalle={expected !== null ? `Esperado hoy: ${Math.round(expected * 100)}%` : 'Sin fechas del proyecto'} />
          <TarjetaIndicador titulo={t('task', true)} valor={`${done}/${top.length}`} icono="mdi:format-list-checks" detalle="completadas" />
          <TarjetaIndicador titulo="Vencidas" valor={top.filter(isOverdue).length} icono="mdi:clock-outline" semaforo={top.some(isOverdue) ? 'RED' : 'GREEN'} />
        </div>
        <Card>
          <CardHeader><CardTitle className="text-base">Descripción</CardTitle></CardHeader>
          <CardContent><VisorTexto html={project.description} /></CardContent>
        </Card>
        <Slot name="project.overview" project={project} />
        <Card>
          <CardHeader><CardTitle className="text-base">Actividad reciente</CardTitle></CardHeader>
          <CardContent>
            <Timeline>
              {activity.slice(0, 15).map((a, i) => (
                <TimelineItem key={a.id} step={i + 1}>
                  <TimelineHeader>
                    <TimelineSeparator />
                    <TimelineDate>{new Date(a.timestamp).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' })}</TimelineDate>
                    <TimelineTitle>{a.actorKind === 'AI' ? 'Motor de decisión' : a.actorKind === 'SYSTEM' ? 'Automatización' : people.get(a.actorId ?? '')?.nombre ?? 'Alguien'}</TimelineTitle>
                    <TimelineIndicator />
                  </TimelineHeader>
                  <TimelineContent>{describeEvent(a, t('team').toLowerCase())}</TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          </CardContent>
        </Card>
      </div>
      <div className="space-y-6">
        <Card>
          <CardContent className="flex items-center gap-4 pt-6">
            <ProgresoCircular valor={progress} color={light} tamano={64} />
            <div className="space-y-1 text-sm">
              <div className="flex items-center gap-2"><Semaforo color={light} conTexto /></div>
              <div className="text-muted-foreground"><Icono nombre="mdi:calendar-range" className="mr-1 inline size-3.5" />{project.startDate ? formatDate(project.startDate, true) : 'Sin inicio'} → {project.dueDate ? formatDate(project.dueDate, true) : 'sin fin'}</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Equipo</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {project.visibility === 'TEAM' && <p className="text-xs text-muted-foreground">Proyecto de equipo: toda persona interna puede colaborar.</p>}
            {project.members.map((m) => (
              <div key={m.userId} className="flex items-center gap-2 text-sm">
                <AvatarPersona persona={{ nombre: m.displayName, color: m.avatarColor, externo: m.external }} tamano="sm" />
                <span className="flex-1 truncate">{m.displayName}</span>
                <span className="text-xs text-muted-foreground">{PROJECT_ROLE_LABEL[m.role]}</span>
              </div>
            ))}
            {project.teams.map((tm) => <div key={tm.teamId} className="flex items-center gap-2 text-sm"><Icono nombre="mdi:account-group-outline" /><span className="flex-1">{tm.name}</span><span className="text-xs text-muted-foreground">{PROJECT_ROLE_LABEL[tm.role]}</span></div>)}
          </CardContent>
        </Card>
        {has('core.goals') && (
          <Card>
            <CardHeader><CardTitle className="text-base">{t('goal', true)} a los que aporta</CardTitle></CardHeader>
            <CardContent className="space-y-1.5 text-sm">
              {links.length ? links.map((l) => <div key={l.link.id} className="flex items-center gap-2"><Icono nombre="mdi:target" className="text-muted-foreground" />{l.code && <span className="tnum text-muted-foreground">{l.code}</span>}<span className="truncate">{l.title}</span></div>) : <p className="text-muted-foreground">Sin vincular. El motor de decisión puede sugerirlo en «Sugerencias».</p>}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
